// CharacterLauncher — the character-select screen. Boot lands here unless a
// ?character= deep link names a character (see src/lib/characterSelection.js).
//
// Why this exists: boot used to resolve `list[0]` whenever there was no saved
// selection, so "no character loaded" was a transient the app rushed through
// rather than a place it could be. That left nowhere to put a picker, which is
// why character switching had to live in an always-mounted floating overlay.
//
// The load-bearing design idea: each card is also a DIAGNOSTIC — a character with
// no renderable geometry is knowable BEFORE the user commits to a load, instead of
// after a multi-second fetch that ends in the CheckMissingRig overlay.
//
// ── Styling note ─────────────────────────────────────────────────────────────
// Laid out to the approved "AvatarOS Shell" Claude Design export. That export
// inlines literal color values, and every one of them turns out to BE a
// default-palette token value — its page/panel/graph backgrounds, its foreground
// and dim-foreground greys, its accent, and the three typed-wire colors all match
// --m-bg / --m-panel / --m-bg-graph / --m-fg / --m-fg-3 / --m-accent /
// --m-wire-anim / --m-wire-output / --m-wire-state exactly. So this uses the
// TOKENS: pixel-identical on the default palette, and unlike inlined literals it
// follows a palette switch.
//
// The few values with no token equivalent are derived with color-mix from the
// nearest one rather than hardcoded, for the same reason. Each is noted below.
//
// Pure presentational: props in, callbacks out. No stores, no fetches. The
// character list, the recents order, and the per-character status are all resolved
// by the caller (see characterRigStatus.js for the status probe). The one
// imperative dependency is dialog.confirm for the delete flow — UI, not data.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CharacterCreationWizard } from './CharacterCreationWizard'
import { dialog } from '../lib/dialog'
import { formatRelativeTime } from '../lib/relativeTime.js'
import CharacterProperties from './CharacterProperties.jsx'
import { cardActivityLabel } from '../lib/characterProperties.js'
import { SCOPE, matchesScope, cardBadge } from '../lib/characterSharing.js'
// Shared with the menubar's character chip (src/components/AppMenuBar.jsx) so one
// character is drawn and labelled identically wherever it is named.
//
// 'unknown' (no probe result yet, or a failed probe) deliberately renders NO badge.
// A false broken badge on a working character is worse than no badge.
import { PipelineGlyph, STATUS, MONO, UI_FONT, statusTone } from './PipelineGlyph.jsx'
import { cacheBustedThumbnailUrl } from '../lib/launcherThumbnailCrop.js'

const LINE = 'var(--m-line)'
// The design's hover border (22% white) and dashed create-card border (18% white)
// have no token; derived from --m-fg so a light palette gets a dark edge rather
// than an invisible white one.
const HOVER_BORDER = 'color-mix(in srgb, var(--m-fg) 22%, transparent)'
const DASHED_BORDER = 'color-mix(in srgb, var(--m-fg) 18%, transparent)'

const NO_RIG = {
  color: 'var(--m-warn)',
  border: 'color-mix(in srgb, var(--m-warn) 40%, transparent)',
  wash: 'color-mix(in srgb, var(--m-warn) 12%, transparent)',
}
// A rigless character reads as a warning at rest, not on hover.
const NO_RIG_CARD_BORDER = 'color-mix(in srgb, var(--m-warn) 45%, transparent)'
// Space the top-right ⋯ menu button occupies. Reserved as padding on the rows
// that hold text, rather than left to overlap: the button is absolutely
// positioned, so without it a long character name simply runs underneath it.
// Was 180 (room for three always-visible DUPLICATE/PROPERTIES/DELETE text
// controls) before those collapsed into one small button (ENG-1044).
const CONTROL_GUTTER = 34

/** The card's one overflow control — a sibling of the card button, never
    nested inside it (a button inside a button is invalid HTML and swallows
    events, same reason the three controls it replaces were laid over the
    card rather than in it).

    Was three always-visible text controls (DUPLICATE / PROPERTIES / DELETE).
    Collapsed to a single ⋯ menu (ENG-1044) for two reasons: three buttons per
    card crowded a dense grid, and DELETE sitting flush against PROPERTIES
    made a routine click one slip away from a destructive one. DELETE now
    lives LAST, below a divider, red — the industry pattern (Figma/Notion/
    Drive/Linear card menus) of putting distance between "look at this" and
    "destroy this" rather than trusting pixel-perfect aim.

    Menu-open state is owned by the launcher (`openMenuId`), not local — the
    same reason `propsId` is: only one card's menu may be open at a time, so
    opening one must close whatever else was open. */
function CardMenu({ character, onRemove, onProperties, onDuplicate, open, onOpen, onClose, disabled = false }) {
  const [busy, setBusy] = useState(false)
  const name = character.label ?? character.id
  const rootRef = useRef(null)
  const inert = busy || disabled

  // Click-outside + Escape both close — the two dismissal gestures every
  // other overlay in this file (e.g. CharacterProperties) already honors.
  useEffect(() => {
    if (!open) return
    const onDocPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose()
    }
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDocPointer)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocPointer)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  // Every handler below stops propagation — this whole control sits on top of
  // a card whose own click starts a several-second character load nobody
  // asked for.
  const stop = (e) => { e.stopPropagation(); e.preventDefault() }

  const toggle = (e) => { stop(e); if (inert) return; open ? onClose() : onOpen() }

  const duplicate = async (e) => {
    stop(e)
    onClose()
    setBusy(true)
    try {
      await onDuplicate(character)
    } finally {
      setBusy(false)
    }
  }

  const properties = (e) => {
    stop(e)
    onClose()
    onProperties(character)
  }

  const requestDelete = async (e) => {
    stop(e)
    onClose()
    // Presence infra isn't deployed, so we can't tell the deleter someone
    // else has this character open right now — the cheap, honest substitute
    // is warning off the `shared` flag the list already carries per row
    // (see the ScopeTabs comment above). Restore-window line always shown:
    // 30 days is the server's delete-retention default (see the delete API's
    // `expires_at` and the docs).
    const sharedWarning = character.shared
      ? ` Shared with the org — someone may have it open right now; their unsaved changes to it will fail after deletion.`
      : ''
    const confirmed = await dialog.confirm({
      title: 'Delete character?',
      body: `This deletes “${name}” and its files from the server for everyone.${sharedWarning} It can be restored from Deleted characters for 30 days. Hold the Delete button to confirm.`,
      confirmLabel: 'Delete',
      danger: true,
      holdToConfirm: {
        confirmId: `remove:${character.id}`,
        testId: `launcher-remove-confirm-${character.id}`,
      },
    })
    if (confirmed) onRemove(character)
  }

  const itemStyle = {
    all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'block',
    width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 3,
    fontFamily: UI_FONT, fontSize: 9.5, letterSpacing: '.04em', whiteSpace: 'nowrap',
    color: 'var(--m-fg-2)',
  }

  return (
    <div ref={rootRef} style={{ position: 'absolute', top: 6, right: 6, zIndex: 3 }}>
      <button
        type="button"
        data-testid={`launcher-menu-${character.id}`}
        title={`Actions for “${name}”`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        disabled={inert}
        style={{
          all: 'unset', boxSizing: 'border-box', cursor: inert ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 20, borderRadius: 4,
          fontFamily: UI_FONT, fontSize: 11, lineHeight: 1,
          color: 'var(--m-fg-2)',
          border: `1px solid ${open ? 'var(--m-line-hi)' : LINE}`,
          background: 'color-mix(in srgb, var(--m-panel-hi) 90%, transparent)',
          opacity: inert ? 0.6 : 1,
        }}
      >{busy ? '…' : '⋯'}</button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            minWidth: 128, borderRadius: 6, padding: 4,
            border: `1px solid ${LINE}`, background: 'var(--m-panel-hi)',
            boxShadow: '0 6px 18px color-mix(in srgb, black 35%, transparent)',
          }}
        >
          {onDuplicate && (
            <button
              type="button" role="menuitem"
              data-testid={`launcher-duplicate-${character.id}`}
              title="Duplicate"
              onClick={duplicate}
              style={itemStyle}
            >⧉ Duplicate</button>
          )}
          {onProperties && (
            <button
              type="button" role="menuitem"
              data-testid={`launcher-props-${character.id}`}
              title={`Properties of “${name}” — who made it and when it was last touched`}
              onClick={properties}
              style={itemStyle}
            >Properties</button>
          )}
          {onRemove && (
            <>
              <div style={{ height: 1, margin: '4px 2px', background: LINE }} />
              {/* LAST and DIVIDED, deliberately: the slip this fixes is a routine
                  click (Properties/Duplicate) landing on Delete because the two
                  sat side by side. Distance + a divider is the fix, not a
                  smaller target. */}
              <button
                type="button" role="menuitem"
                data-testid={`launcher-remove-${character.id}`}
                title={`Delete “${name}” from the server`}
                onClick={requestDelete}
                style={{ ...itemStyle, color: 'var(--m-danger)' }}
              >Delete</button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Renderer-type chip. `large` picks the two size steps the design specifies. */
function TypeTag({ status, large }) {
  const spec = STATUS[status]
  if (!spec) return null
  const c = `var(${spec.token})`
  return (
    <span style={{
      fontFamily: UI_FONT,
      fontSize: large ? 8.5 : 8,
      letterSpacing: '.10em',
      padding: large ? '2.5px 5px' : '2px 4px',
      borderRadius: 3,
      whiteSpace: 'nowrap',
      color: c,
      border: `1px solid color-mix(in srgb, ${c} 40%, transparent)`,
      background: `color-mix(in srgb, ${c} 10%, transparent)`,
    }}>{spec.label}</span>
  )
}

function NoRigTag({ large }) {
  return (
    <span style={{
      fontFamily: UI_FONT,
      fontSize: large ? 8.5 : 8,
      letterSpacing: '.08em',
      padding: large ? '2.5px 5px' : '2px 4px',
      borderRadius: 3,
      whiteSpace: 'nowrap',
      color: NO_RIG.color,
      border: `1px solid ${NO_RIG.border}`,
      background: NO_RIG.wash,
    }}>⚠ NO RIG</span>
  )
}

/** The dotted well behind a placeholder. --m-grid-img IS the design's pattern. */
const well = (dot) => ({
  background: 'var(--m-bg-graph)',
  backgroundImage: 'var(--m-grid-img)',
  backgroundSize: `${dot}px ${dot}px`,
})

function useHover() {
  const [hover, setHover] = useState(false)
  return [hover, { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) }]
}

/** Card imagery (ENG-1079): the character's captured launcher thumbnail, or the
 *  PipelineGlyph fallback. Absence is data — no thumbnail, or a broken/failed
 *  image URL, both render the glyph, never a broken <img>. `key`ed on src by the
 *  caller when the URL can change so the failed-state resets on a new capture. */
function CardThumb({ src, status, glyphSize, version }) {
  const [failed, setFailed] = useState(false)
  if (src && !failed) {
    return (
      <img
        src={cacheBustedThumbnailUrl(src, version)}
        alt=""
        draggable={false}
        onError={() => setFailed(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    )
  }
  return <PipelineGlyph status={status} size={glyphSize} />
}

/** Hover glimpse (ENG-1079): a larger portrait of a compact card's thumbnail,
 *  shown on mouseover. position:fixed + a bounding-rect anchor so it escapes the
 *  scroll container's clipping and never adds layout; pointer-events none so it
 *  can't steal the hover. Only rendered when the card actually has a thumbnail. */
function CardHoverPreview({ anchorRef, src, version }) {
  const [pos, setPos] = useState(null)
  useEffect(() => {
    const el = anchorRef.current
    if (!el || typeof window === 'undefined') return
    const PW = 168, PH = 224, GAP = 10
    const r = el.getBoundingClientRect()
    // Prefer the right of the card; flip to the left when there's no room.
    let left = r.right + GAP
    if (left + PW > window.innerWidth - 8) left = r.left - GAP - PW
    let top = r.top + r.height / 2 - PH / 2
    top = Math.max(8, Math.min(top, window.innerHeight - PH - 8))
    setPos({ left, top, w: PW, h: PH })
  }, [anchorRef])
  if (!pos) return null
  return (
    <div
      data-testid="launcher-hover-preview"
      style={{
        position: 'fixed', left: pos.left, top: pos.top, width: pos.w, height: pos.h,
        zIndex: 60, pointerEvents: 'none', borderRadius: 8, overflow: 'hidden',
        border: '1px solid var(--m-line-hi)', background: 'var(--m-panel)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        animation: 'aos-launcher-preview-in 120ms ease-out',
      }}
    >
      <img src={cacheBustedThumbnailUrl(src, version)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </div>
  )
}

/** Large RECENT card: glyph well, then name+id, then tags+when. */
function RecentCard({ character, status, openedLabel, onOpen, onRemove, onProperties, onDuplicate, badge, menuOpen, onMenuOpen, onMenuClose, pendingOp }) {
  const [hover, hoverProps] = useHover()
  const missing = status === 'missing'
  const name = character.label ?? character.id
  const deleting = pendingOp === 'deleting'
  const duplicating = pendingOp === 'duplicating'

  return (
    <div style={{ position: 'relative' }} {...hoverProps}>
      {/* No menu at all while deleting — there is nothing left to act on. */}
      {!deleting && (onRemove || onProperties || onDuplicate) && (
        <CardMenu
          character={character}
          onRemove={onRemove}
          onProperties={onProperties}
          onDuplicate={onDuplicate}
          open={menuOpen}
          onOpen={onMenuOpen}
          onClose={onMenuClose}
          disabled={duplicating}
        />
      )}
      <button
        type="button"
        data-testid={`launcher-card-${character.id}`}
        data-rig-missing={missing ? '1' : '0'}
        onClick={() => onOpen(character)}
        disabled={deleting}
        title={missing ? `${name} — no renderable geometry yet` : `Open ${name}`}
        style={{
          all: 'unset', boxSizing: 'border-box', cursor: deleting ? 'default' : 'pointer', width: '100%',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          borderRadius: 7,
          opacity: deleting ? 0.45 : 1,
          pointerEvents: deleting ? 'none' : 'auto',
          background: hover ? 'var(--m-panel-hi)' : 'var(--m-panel)',
          border: `1px solid ${missing ? NO_RIG_CARD_BORDER : hover ? HOVER_BORDER : LINE}`,
          transition: 'background .12s, border-color .12s',
        }}
      >
        <span style={{
          ...well(8),
          aspectRatio: '3 / 4', width: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          borderBottom: `1px solid ${LINE}`,
        }}>
          <CardThumb key={`${character.thumbnailUrl || 'glyph'}:${character.updatedAt || ''}`} src={character.thumbnailUrl} version={character.updatedAt} status={status} glyphSize={56} />
        </span>

        <span style={{ display: 'grid', gap: 7, padding: '10px 11px 11px' }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{
              fontSize: 12.5, fontWeight: 500, color: 'var(--m-fg)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{name}</span>
            <span style={{ flex: 1 }} />
            <code style={{ fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)', whiteSpace: 'nowrap' }}>
              {character.id}
            </code>
          </span>

          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <TypeTag status={status} large />
            {missing && <NoRigTag large />}
            {badge && <PrivateTag badge={badge} />}
            {duplicating && <DuplicatingTag id={character.id} />}
            <span style={{ flex: 1 }} />
            {/* Omitted rather than faked when the timestamp is unknown — legacy
                recents entries carry no `at` (see readRecentCharacters). */}
            {openedLabel && (
              <code style={{ fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)', whiteSpace: 'nowrap' }}>
                opened {openedLabel}
              </code>
            )}
          </span>
        </span>
      </button>
      {deleting && (
        <span
          data-testid={`launcher-deleting-${character.id}`}
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: UI_FONT, fontSize: 11, letterSpacing: '.12em', color: 'var(--m-fg-2)',
            animation: 'aos-launcher-pulse 1.4s ease-in-out infinite', pointerEvents: 'none',
          }}
        >DELETING…</span>
      )}
    </div>
  )
}

/**
 * Compact card: a type RAIL rather than a thumbnail tile.
 *
 * The 3px left edge carries the pipeline color, and the name takes back the space
 * a 40px tile used to occupy. It is also the only treatment where a rigless
 * character is unmissable — the rail goes amber and is the tallest element on the
 * card, so a broken character cannot hide in a long grid.
 */
/** PRIVATE badge (ENG-1028). Only private characters are badged: shared is the
    norm, and a badge on every card is noise that hides the ones that matter.
    Uses --m-accent so it reads as a state rather than as a warning — a private
    character is working as intended, unlike the NO RIG tag beside it. */
/** Badge for a card whose duplicate is in flight (ENG-1044 Task 11). Pulses via
    the shared `aos-launcher-pulse` keyframes (defined once, at the bottom of
    the launcher's render) rather than a static label — the duplicate call is
    observed to take ~13s on staging, and a static "DUPLICATING" reads as dead
    chrome over that long a wait. */
function DuplicatingTag({ id }) {
  return (
    <span
      data-testid={`launcher-duplicating-${id}`}
      style={{
        fontFamily: UI_FONT, fontSize: 7.5, letterSpacing: '.08em',
        color: 'var(--m-fg-2)', border: `1px solid ${LINE}`, borderRadius: 3,
        padding: '1px 4px', whiteSpace: 'nowrap',
        animation: 'aos-launcher-pulse 1.4s ease-in-out infinite',
      }}
    >DUPLICATING…</span>
  )
}

/** Ghost card standing in for a duplicate-in-progress copy, shown in the main
    grid immediately after (or, if the source card is only in RECENT, appended
    to) the CHARACTERS grid — the copy lands there, not in RECENT, once the
    refresh picks it up. Purely a placeholder: no click target, no server row
    behind it yet, gone the instant `pendingOps` clears because the real card
    (server truth) takes its place. `name` is the name the user chose in the
    duplicate prompt (ENG-1044 Task 14) — shown verbatim, since it is already
    the full name the copy will land under, not the source's label. */
function PendingCopyCard({ id, name }) {
  return (
    <div
      data-testid={`launcher-pending-copy-${id}`}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        minHeight: 60, boxSizing: 'border-box', borderRadius: 6,
        border: `1px dashed ${DASHED_BORDER}`, background: 'transparent',
        fontFamily: UI_FONT, fontSize: 9.5, letterSpacing: '.04em', color: 'var(--m-fg-3)',
        padding: '9px 10px',
      }}
    >
      <span style={{
        width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
        border: '1.5px solid color-mix(in srgb, var(--m-fg-3) 30%, transparent)',
        borderTopColor: 'var(--m-fg-3)',
        animation: 'aos-launcher-spin 0.8s linear infinite',
      }} />
      <span>Creating &ldquo;{name}&rdquo;…</span>
    </div>
  )
}

function PrivateTag({ badge }) {
  return (
    <span
      data-testid={`launcher-private-${badge.key}`}
      title={badge.title}
      style={{
        fontFamily: UI_FONT, fontSize: 7.5, letterSpacing: '.1em',
        color: 'var(--m-accent)', border: '1px solid var(--m-accent)',
        borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap',
        // Derived from the token rather than hardcoded, same rule as the file's
        // other non-token values.
        background: 'color-mix(in srgb, var(--m-accent) 12%, transparent)',
      }}
    >{badge.label}</span>
  )
}

function CompactCard({ character, status, onOpen, onRemove, onProperties, onDuplicate, activityLabel, badge, menuOpen, onMenuOpen, onMenuClose, pendingOp }) {
  const [hover, hoverProps] = useHover()
  const wrapRef = useRef(null)
  const missing = status === 'missing'
  const name = character.label ?? character.id
  const rail = statusTone(status)
  const deleting = pendingOp === 'deleting'
  const duplicating = pendingOp === 'duplicating'

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} {...hoverProps}>
      {/* Hover glimpse of the launcher thumbnail (ENG-1079). */}
      {hover && character.thumbnailUrl && !deleting && (
        <CardHoverPreview anchorRef={wrapRef} src={character.thumbnailUrl} version={character.updatedAt} />
      )}
      {!deleting && (onRemove || onProperties || onDuplicate) && (
        <CardMenu
          character={character}
          onRemove={onRemove}
          onProperties={onProperties}
          onDuplicate={onDuplicate}
          open={menuOpen}
          onOpen={onMenuOpen}
          onClose={onMenuClose}
          disabled={duplicating}
        />
      )}
      <button
        type="button"
        data-testid={`launcher-card-${character.id}`}
        data-rig-missing={missing ? '1' : '0'}
        onClick={() => onOpen(character)}
        disabled={deleting}
        title={missing ? `${name} — no renderable geometry yet` : `Open ${name}`}
        style={{
          all: 'unset', boxSizing: 'border-box', cursor: deleting ? 'default' : 'pointer', width: '100%',
          // A thumbnail (ENG-1079), when present, adds a 40px tile between the
          // pipeline rail and the text; without one the card keeps its
          // deliberate rail-only treatment (the rigless-amber-rail signal).
          display: 'grid',
          gridTemplateColumns: character.thumbnailUrl ? '3px 46px 1fr' : '3px 1fr',
          alignItems: 'stretch',
          overflow: 'hidden', borderRadius: 6, minHeight: 60,
          opacity: deleting ? 0.45 : 1,
          pointerEvents: deleting ? 'none' : 'auto',
          background: hover ? 'var(--m-panel-hi)' : 'var(--m-panel)',
          border: `1px solid ${missing ? NO_RIG_CARD_BORDER : hover ? HOVER_BORDER : LINE}`,
          transition: 'background .12s, border-color .12s',
        }}
      >
        <span style={{ background: rail, opacity: missing ? 1 : 0.75 }} />
        {character.thumbnailUrl && (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRight: `1px solid ${LINE}` }}>
            <CardThumb key={`${character.thumbnailUrl}:${character.updatedAt || ''}`} src={character.thumbnailUrl} version={character.updatedAt} status={status} glyphSize={22} />
          </span>
        )}
        <span style={{ display: 'grid', gap: 5, padding: '9px 11px', minWidth: 0 }}>
          {/* id sits INLINE after the name here — unlike the large card, where it is
              pushed to the right edge. */}
          <span style={{
            display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0,
            // Keeps the name and id clear of the absolutely-positioned
            // ⋯ menu button above them.
            paddingRight: onProperties || onRemove || onDuplicate ? CONTROL_GUTTER : 0,
          }}>
            <span style={{
              fontSize: 12, fontWeight: 500, color: 'var(--m-fg)', letterSpacing: '-.01em',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{name}</span>
            <code style={{
              fontFamily: MONO, fontSize: 8.5, color: 'var(--m-fg-3)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{character.id}</code>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <TypeTag status={status} />
            {missing && <NoRigTag />}
            {badge && <PrivateTag badge={badge} />}
            {duplicating && <DuplicatingTag id={character.id} />}
            <span style={{ flex: 1 }} />
            {/* The triage number, on the card so a long grid is scannable
                without opening anything. Omitted rather than explained when
                absent — a card has no room for a reason, and the Properties
                panel gives one. */}
            {activityLabel && (
              <code style={{
                fontFamily: MONO, fontSize: 8.5, color: 'var(--m-fg-3)', whiteSpace: 'nowrap',
              }}>{activityLabel}</code>
            )}
          </span>
        </span>
      </button>
      {deleting && (
        <span
          data-testid={`launcher-deleting-${character.id}`}
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: UI_FONT, fontSize: 10, letterSpacing: '.1em', color: 'var(--m-fg-2)',
            animation: 'aos-launcher-pulse 1.4s ease-in-out infinite', pointerEvents: 'none',
          }}
        >DELETING…</span>
      )}
    </div>
  )
}

/** Scope tabs (ENG-1028).
 *
 *  Client-side: the list already carries `shared` and `ownerId` for every row
 *  the caller can see, so filtering here costs one pass and no request — and
 *  asking the server per tab would make a tab switch a loading state for data
 *  already in memory.
 *
 *  MINE is "owned by me", NOT "private to me": a character you own and have
 *  published is still yours, and having it leave your tab the moment you share
 *  it would make the tab useless as a working set. The counts are shown because
 *  an empty tab with no number reads as broken rather than as empty.
 */
function ScopeTabs({ scope, onScope, counts }) {
  const TABS = [
    [SCOPE.ALL, 'ALL'],
    [SCOPE.MINE, 'MINE'],
    [SCOPE.SHARED, 'SHARED'],
  ]
  return (
    <div data-testid="launcher-scope-tabs" style={{ display: 'flex', gap: 2 }}>
      {TABS.map(([key, label]) => {
        const active = scope === key
        return (
          <button
            key={key}
            type="button"
            data-testid={`launcher-scope-${key}`}
            data-active={active ? '1' : '0'}
            onClick={() => onScope(key)}
            style={{
              all: 'unset', boxSizing: 'border-box', cursor: 'pointer',
              padding: '4px 9px', borderRadius: 4,
              fontFamily: UI_FONT, fontSize: 8.5, letterSpacing: '.1em',
              color: active ? 'var(--m-fg)' : 'var(--m-fg-3)',
              background: active ? 'var(--m-panel-hi)' : 'transparent',
              border: `1px solid ${active ? 'var(--m-line-hi)' : 'transparent'}`,
            }}
          >
            {label}
            <span style={{ fontFamily: MONO, fontSize: 8, marginLeft: 5, opacity: 0.7 }}>
              {counts[key]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '0 0 11px' }}>
      <span style={{
        fontFamily: UI_FONT, fontSize: 9.5, letterSpacing: '.14em',
        color: 'var(--m-fg-3)', whiteSpace: 'nowrap',
      }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: LINE }} />
    </div>
  )
}

/**
 * Props:
 *   characters   — the server character list (non-empty; the zero case is
 *                  NoCharactersScreen, a different state).
 *   recents      — [{ id, at }] newest first, from readRecentCharacters().
 *                  Unknown ids are ignored; a null `at` renders no "opened" line.
 *   statusById   — Map|object of id → 'rig'|'splat'|'stream'|'missing'|'unknown'
 *                  from probeRigStatuses(). Absent/'unknown' renders no badge.
 *   now          — epoch ms for relative-time labels; injectable for tests.
 *   onOpen(c)    — user picked a character.
 *   onCreated(c) — the creation wizard produced a new character.
 *   onRemove(c)  — delete this character on the server. Omit to hide the control
 *                  entirely; when present it is gated behind a confirm dialog
 *                  whose confirm button must be HELD — never a single click.
 *   onDuplicate(c) — copy this character on the server; returns a Promise the
 *                  control awaits to show a busy state. Omit to hide the
 *                  control entirely. Non-destructive: no confirm, no hold.
 *
 * Provenance (ENG-1032) is read off the character rows themselves — createdAt /
 * createdBy / updatedAt / updatedBy / lastActivityAt from GET /api/characters —
 * so the Properties panel needs no extra fetch and no extra prop. A row that
 * carries none of it renders an explicit, reasoned "unknown"; see
 * src/lib/characterProperties.js.
 */
export default function CharacterLauncher({
  characters = [], recents = [], statusById, now, onOpen, onCreated, onRemove, onDuplicate,
  // Personal workspaces (ENG-1028). All optional: without them the launcher
  // renders exactly as it did before the feature — no scope tabs, no badges, no
  // visibility control — rather than showing dead chrome.
  viewerId, viewerEmail, isOrgAdmin = false, loadShareImpact, onSetShared, onAssignOwner,
  // id -> 'deleting' | 'duplicating' (ENG-1044 Task 11). App owns the op's
  // lifetime — it's set before the first await and cleared on every exit path,
  // including a refresh failure — this component only reads it to render
  // per-card in-flight feedback and a ghost placeholder for a duplicate whose
  // copy hasn't landed in `characters` yet.
  pendingOps = {},
  // id -> the name the user chose in the duplicate name prompt (ENG-1044
  // Task 14), parallel to `pendingOps` so a 'deleting' entry stays a bare
  // string. Only ever read for an id whose pendingOps value is
  // 'duplicating'; PendingCopyCard falls back to "{label} copy" if a name
  // is somehow missing rather than rendering blank.
  pendingNames = {},
}) {
  const [query, setQuery] = useState('')
  // Scope defaults to ALL so the first render matches the pre-ENG-1028 list.
  // Switching to MINE is a deliberate act, not something the app decides for
  // someone who has not asked to narrow their view.
  const [scope, setScope] = useState(SCOPE.ALL)
  const [showWizard, setShowWizard] = useState(false)
  const [createHover, setCreateHover] = useState(false)
  // The character whose Properties panel is open, or null. Held by id rather
  // than by object so a refreshed `characters` list re-resolves to the new row
  // instead of pinning a stale copy behind an open panel.
  const [propsId, setPropsId] = useState(null)
  // The card whose ⋯ menu is open, or null. Lifted here rather than local to
  // each card for the same reason `propsId` is lifted: only one may be open,
  // so opening a second card's menu must close whatever was already open.
  const [openMenuId, setOpenMenuId] = useState(null)

  // Accept a Map or a plain object so the caller isn't forced into either.
  const statusOf = (id) => {
    if (!statusById) return 'unknown'
    const v = typeof statusById.get === 'function' ? statusById.get(id) : statusById[id]
    return v ?? 'unknown'
  }

  const { recent, rest, missingCount } = useMemo(() => {
    const q = query.trim().toLowerCase()
    const match = c => !q
      || c.id.toLowerCase().includes(q)
      || String(c.label ?? '').toLowerCase().includes(q)

    const visible = characters.filter(c => match(c) && matchesScope(c, scope, viewerId))
    const byId = new Map(visible.map(c => [c.id, c]))
    // Unknown recent ids (deleted characters) drop out rather than rendering a
    // dead card — the list is the source of truth, recents are just an order.
    const rec = []
    for (const entry of recents) {
      const c = byId.get(entry.id)
      if (c) rec.push({ character: c, at: entry.at })
    }
    // The design's RECENT row is a fixed 3 columns, so take at most 3.
    const recTop = rec.slice(0, 3)
    const recSet = new Set(recTop.map(r => r.character.id))

    return {
      recent: recTop,
      rest: visible.filter(c => !recSet.has(c.id)),
      // Only CONFIRMED-missing characters are counted. An unprobed or
      // failed-probe character is 'unknown' and must not inflate this number.
      missingCount: characters.filter(c => statusOf(c.id) === 'missing').length,
    }
  }, [characters, recents, query, statusById, scope, viewerId])

  // Counts are over the WHOLE list, not the text-filtered one: a tab's number
  // answers "how many are there", and having it move as you type would make it
  // impossible to tell an empty tab from a non-matching search.
  const scopeCounts = useMemo(() => ({
    [SCOPE.ALL]: characters.length,
    [SCOPE.MINE]: characters.filter(c => matchesScope(c, SCOPE.MINE, viewerId)).length,
    [SCOPE.SHARED]: characters.filter(c => matchesScope(c, SCOPE.SHARED, viewerId)).length,
  }), [characters, viewerId])

  const noMatches = recent.length === 0 && rest.length === 0
  const propsCharacter = propsId == null ? null : characters.find(c => c.id === propsId) ?? null
  const openProperties = (c) => setPropsId(c.id)

  return (
    <div
      data-testid="character-launcher"
      style={{
        width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', background: 'var(--m-bg)', color: 'var(--m-fg)',
        fontFamily: 'var(--m-font)',
      }}
    >
      {/* ---- Top bar ---- */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0,
        padding: '11px 15px', borderBottom: `1px solid ${LINE}`, background: 'var(--m-panel)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{
            fontFamily: UI_FONT, fontSize: 12.5, letterSpacing: '.18em',
            fontWeight: 500, color: 'var(--m-fg)',
          }}>AVATAROS</span>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--m-accent)' }} />
        </div>

        <div style={{ position: 'relative', flex: 1, maxWidth: 400, display: 'flex', alignItems: 'center' }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="⌕  Filter characters…"
            aria-label="Filter characters"
            data-testid="launcher-filter"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--m-panel-hi)', color: 'var(--m-fg)',
              border: '1px solid var(--m-line-hi)', borderRadius: 5,
              padding: '5px 40px 5px 9px', fontFamily: UI_FONT, fontSize: 10.5, outline: 'none',
            }}
          />
          {/* Inside the field, per the design. A hint only — there is no command
              palette yet, so it is deliberately not wired to anything. */}
          <span style={{
            position: 'absolute', right: 6, pointerEvents: 'none',
            fontFamily: MONO, fontSize: 8.5, color: 'var(--m-fg-3)',
            border: `1px solid ${LINE}`, borderRadius: 3, padding: '2px 4px',
          }}>⌘K</span>
        </div>

        {/* Only when the caller identified the viewer — without an id, MINE
            cannot be computed and the tabs would be a lie. */}
        {viewerId && <ScopeTabs scope={scope} onScope={setScope} counts={scopeCounts} />}

        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--m-fg-3)' }}>
          {characters.length} character{characters.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* ---- Body ---- */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 15px 24px' }}>
        {recent.length > 0 && (
          <>
            <SectionLabel>RECENT</SectionLabel>
            {/* Portrait character tiles (ENG-1079): narrow, left-aligned, so a
                standing avatar reads head-to-toe instead of being cropped to a
                mid-body band in a wide well. */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 190px))',
              justifyContent: 'start', gap: 14, marginBottom: 26,
            }}>
              {recent.map(({ character, at }) => (
                <RecentCard
                  key={character.id}
                  character={character}
                  status={statusOf(character.id)}
                  openedLabel={at == null ? null : formatRelativeTime(at, now)}
                  badge={cardBadge(character, viewerId)}
                  onOpen={onOpen}
                  onRemove={onRemove}
                  onProperties={openProperties}
                  onDuplicate={onDuplicate}
                  menuOpen={openMenuId === character.id}
                  onMenuOpen={() => setOpenMenuId(character.id)}
                  onMenuClose={() => setOpenMenuId(null)}
                  pendingOp={pendingOps[character.id]}
                />
              ))}
            </div>
          </>
        )}

        {!noMatches && (
          <SectionLabel>{recent.length > 0 ? 'ALL CHARACTERS' : 'CHARACTERS'}</SectionLabel>
        )}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(258px, 1fr))', gap: 9,
        }}>
          {rest.map(c => {
            const pendingOp = pendingOps[c.id]
            return (
              // Fragment, not a wrapping div: the placeholder must be a sibling
              // GRID CELL right after its source, not nested inside one.
              <React.Fragment key={c.id}>
                <CompactCard
                  character={c}
                  status={statusOf(c.id)}
                  activityLabel={cardActivityLabel(c, now)}
                  badge={cardBadge(c, viewerId)}
                  onOpen={onOpen}
                  onRemove={onRemove}
                  onProperties={openProperties}
                  onDuplicate={onDuplicate}
                  menuOpen={openMenuId === c.id}
                  onMenuOpen={() => setOpenMenuId(c.id)}
                  onMenuClose={() => setOpenMenuId(null)}
                  pendingOp={pendingOp}
                />
                {pendingOp === 'duplicating' && (
                  <PendingCopyCard id={c.id} name={pendingNames[c.id] ?? `${c.label ?? c.id} copy`} />
                )}
              </React.Fragment>
            )
          })}

          {/* A duplicate whose SOURCE card is only in RECENT (not in `rest`)
              still gets its placeholder here — the copy will land in this
              grid once the refresh picks it up, never in RECENT. */}
          {Object.keys(pendingOps).map(id => {
            if (pendingOps[id] !== 'duplicating') return null
            if (rest.some(c => c.id === id)) return null
            const source = characters.find(c => c.id === id)
            if (!source) return null
            return (
              <PendingCopyCard
                key={`pending-${id}`}
                id={id}
                name={pendingNames[id] ?? `${source.label ?? source.id} copy`}
              />
            )
          })}

          {/* A cell in the same grid, so it sizes like a compact card. */}
          <div
            role="button"
            tabIndex={0}
            data-testid="launcher-create"
            onClick={() => setShowWizard(true)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowWizard(true) } }}
            onMouseEnter={() => setCreateHover(true)}
            onMouseLeave={() => setCreateHover(false)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              padding: '9px 10px', minHeight: 60, boxSizing: 'border-box',
              border: `1px dashed ${createHover ? 'var(--m-accent)' : DASHED_BORDER}`,
              borderRadius: 6, background: 'transparent', cursor: 'pointer',
              fontFamily: UI_FONT, fontSize: 9.5, letterSpacing: '.12em',
              color: createHover ? 'var(--m-accent)' : 'var(--m-fg-3)',
              transition: 'border-color .12s, color .12s',
            }}
          >+ CREATE CHARACTER</div>
        </div>

        {noMatches && (
          <div style={{
            fontFamily: UI_FONT, fontSize: 11, color: 'var(--m-fg-3)', padding: '22px 2px 0',
          }}>
            No character matches “{query}”.
          </div>
        )}
      </div>

      {/* ---- Status bar. No separator glyph — spacing does the work. ---- */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
        padding: '6px 14px', borderTop: `1px solid ${LINE}`, background: 'var(--m-panel)',
        fontFamily: UI_FONT, fontSize: 9.5, color: 'var(--m-fg-3)',
        whiteSpace: 'nowrap', overflow: 'hidden',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: 'var(--m-online)',
            boxShadow: '0 0 5px color-mix(in srgb, var(--m-online) 60%, transparent)',
          }} />
          asset-manager connected
        </span>
        {missingCount > 0 && (
          <span style={{ color: 'var(--m-warn)' }}>
            {missingCount} character{missingCount === 1 ? '' : 's'} missing a rig
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span>no character loaded</span>
      </div>

      {showWizard && (
        <CharacterCreationWizard
          onCreated={config => { setShowWizard(false); onCreated?.(config) }}
          onCancel={() => setShowWizard(false)}
        />
      )}

      {propsCharacter && (
        <CharacterProperties
          character={propsCharacter}
          status={statusOf(propsCharacter.id)}
          now={now}
          viewerId={viewerId}
          viewerEmail={viewerEmail}
          isOrgAdmin={isOrgAdmin}
          loadImpact={loadShareImpact}
          onSetShared={onSetShared}
          onAssignOwner={onAssignOwner}
          onClose={() => setPropsId(null)}
        />
      )}

      {/* Shared by the DELETING overlay, the DUPLICATING badge, and the pending
          copy card's spinner — same idiom as DirtyBadge's inline keyframes. */}
      <style>{`
        @keyframes aos-launcher-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .45; } }
        @keyframes aos-launcher-spin { to { transform: rotate(360deg); } }
        @keyframes aos-launcher-preview-in { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  )
}
